import { AnswerType, CheckboxGroupField as FieldDefinition } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import Checkbox from '@platform-ui-common/components/Checkbox/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import useHelperTextId from '@platform-ui-common/hooks/contexts/useHelperTextId.js';
import {
  GenericFormData,
  addRepeated,
  fieldUpdate,
  removeRepeated,
} from '@platform-ui-common/hooks/useForm/index.js';
import Color, { BorderColors } from '@platform-ui-common/utilities/Color.js';
import { safeKey } from '@platform-ui/utilities/application/utils.js';
import cx from 'classnames';
import { useState } from 'react';
import FullWidthPanel from '../../../components/FullWidthPanel/index.js';
import { FieldProps } from './types.js';

export default function CheckboxGroupField<FormData extends GenericFormData>({
  field: { key, options },
  question,
  answers,
  dispatch,
  errors,
  setValidationOverrides: _,
}: FieldProps<FieldDefinition, FormData>): JSX.Element {
  // NOTE: This is pretty arbitrary, just picked 6 because that's the longest
  // non-collapsed checkbox group
  const shouldCollapseAnswers = options.length > 6;
  const [expanded, setExpanded] = useState(!shouldCollapseAnswers);
  const parsedAnswers = answers[key]?.map((answer) =>
    typeof answer === 'number' ? answer.toString() : answer,
  );

  const onChange = (value: string): void => {
    if (value === AnswerType.None) {
      dispatch(fieldUpdate(key, [AnswerType.None] as FormData[typeof key]));
    }
    if (value === AnswerType.Decline) {
      dispatch(fieldUpdate(key, [AnswerType.Decline] as FormData[typeof key]));
    }
    if (parsedAnswers?.includes(value)) {
      dispatch(removeRepeated(key, answers[key].indexOf(value)));
    } else if (!([AnswerType.None, AnswerType.Decline] as string[]).includes(value)) {
      dispatch(addRepeated(key, value));
    }
  };

  const helperTextId = useHelperTextId();

  const selectAllId = `${safeKey(key)}-select-all`;
  return (
    <div
      className="flex flex-col w-full items-center gap-6 mb-4
                    lg:w-7/12 lg:gap-4 lg:min-w-[30rem] lg:self-center lg:mb-6 lg:px-4"
    >
      <div className="flex flex-col w-full gap-3 lg:gap-4">
        <div className="text-center lg:text-left">
          <Typography id={selectAllId} variant="largeBody" alignment="center">
            Please select all that apply*:
          </Typography>
        </div>
        <FullWidthPanel>
          <div
            className="w-full flex flex-col gap-y-5"
            role="group"
            aria-labelledby={['form-page-title', question?.copy?.intro && 'question-intro']
              .filter(Boolean)
              .join(' ')}
            aria-describedby={selectAllId}
            aria-invalid={!!errors[key]}
            aria-errormessage={helperTextId}
          >
            {(expanded ? options : options.slice(0, 6)).map(
              ({ label, value, description }, idx) => {
                const reducedOpacityStyles = !expanded && idx === 5 ? 'opacity-25' : '';
                const disabled =
                  (value !== AnswerType.None && answers[key].includes(AnswerType.None)) ||
                  (value !== AnswerType.Decline && answers[key].includes(AnswerType.Decline));
                const checked = parsedAnswers?.includes(value);
                return (
                  <div
                    className={cx('flex', reducedOpacityStyles, {
                      [BorderColors[Color.Warning].toString()]: errors[key],
                      'border-[1px]': errors[key],
                    })}
                    key={`checkbox-div-wrapper-${label}`}
                  >
                    <Checkbox
                      label={
                        <span className="flex flex-col gap-1">
                          <Typography variant="body" tag="span" bold={!!description}>
                            {label}
                          </Typography>
                          {description && (
                            <Typography variant="body" tag="span">
                              {description}
                            </Typography>
                          )}
                        </span>
                      }
                      onClick={(_: boolean): void => onChange(value)}
                      checked={checked}
                      disabled={disabled}
                      key={`checkbox-${label}`}
                    />
                  </div>
                );
              },
            )}
          </div>
        </FullWidthPanel>
        {shouldCollapseAnswers && (
          <Button
            variant="text"
            textColor={Color.Link}
            onClick={(): void => setExpanded((prev) => !prev)}
            icon={expanded ? 'keyboardArrowUp' : 'keyboardArrowDown'}
            iconPosition="end"
          >
            {expanded ? 'See less' : 'See more'}
          </Button>
        )}
        {errors[key] && (
          <Typography id={helperTextId} variant="body" color={Color.Error}>
            {errors[key] as string}
          </Typography>
        )}
      </div>
    </div>
  );
}
